import { Box, Grid, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/**API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import { getBranch } from 'utils/app.utils'

/**COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import DoctorFeeList from './DoctorFeeList'

/**STYLE */
import ComponentApi from 'api/components-api'
import FunctionPrint from 'component/Print/print'
import AutocompleteSelect from 'component/Select/AutocompleteSelect'
import * as UseStyled from 'features/report/useStyled'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import { exportDoctorFeeToExcel } from './exportDoctorFee'

type Doctors = {
  userId: number
  firstname: string
  lastname: string
  fullname: string
}

export interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

export default function DoctorFee() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const momentDate = () => momentNowRange
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [operativeCode, setOperativeCode] = useState<any[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [sortBy, setSortBy] = useState<string>('createAt')
  const [sortType, setSortType] = useState<string>('ASC')
  const [doctors, setDoctors] = useState<Doctors[]>([])
  const [fetch, setFetch] = useState(false)
  const [rows, setRows] = useState<any[]>([])
  const [rowsExport, setRowsExport] = useState<any[]>([])
  const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(true)
  const [selectDoctors, setSelectDoctors] = useState<any[]>([])
  const [codeForm, setCodeForm] = useState<{ operativeCode: string }>({ operativeCode: 'ALL' })
  const [codeTo, setCodeTo] = useState<{ operativeCode: string }>({ operativeCode: 'ALL' })
  const [doctorsMultiple, setDoctorsMultiple] = useState<DentistInterface[]>([])
  const [openFilterDoctors, setOpenFilterDoctors] = useState(false)

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'DOCTOR_FEE') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    ClinicApi.findAllBranches()
      .then(({ data }) => {
        setBranches(data)
      })
      .catch((e) => {
        return
      })
    ComponentApi.findAllOperativeCode()
      .then(({ data }) => setOperativeCode([{ operativeCode: "ALL" }, ...data]))
      .catch((e) => {
        return
      })
    ClinicApi.findAllDoctors('', '', rangeDate[0], rangeDate[1], branchId)
      .then((resp) => {
        setDoctors([...resp.data])
        setSelectDoctors(['ALL', ...resp.data.map((d: any) => d.userId)])
        setFetch(true)
      })
      .catch((e) => {
        return
      })
    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [branchId])

  const fetchDF = useCallback(async () => {
    setLoading(true)
    let condition: any = {
      page: page,
      pageLimit: pageLimit,
      branchId: branchId,
      dateStart: rangeDate[0],
      dateEnd: rangeDate[1],
      codeForm: codeForm?.operativeCode && codeForm?.operativeCode !== "ALL" ? codeForm?.operativeCode : '',
      codeTo: codeTo?.operativeCode && codeTo?.operativeCode !== "ALL" ? codeTo?.operativeCode : ''
    }
    if (doctors) {
      const doctor = _.filter(selectDoctors, (v: any) => v > 0).join(',')
      if (doctor) condition = { ...condition, doctorId: _.filter(selectDoctors, (v: any) => v > 0).join(',') }
    }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    ReportApi.reportDoctorFee(condition)
      .then(({ headers, data }) => {
        setRows(data.rows)
        setRowsExport(data.rowsExport)
        setRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => {
        setLoading(false)
      })
    setFetch(false)
  }, [page, pageLimit, branchId, sortBy, sortType, fetch])

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
    setPage(1)
    setFetch(true)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
    setPage(1)
    setFetch(true)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
    setFetch(true)
  }


  useEffect(() => {
    if (fetch) fetchDF()
  }, [fetchDF])

  const handleExport = () => {
    exportDoctorFeeToExcel(rowsExport, t)
  };


  return (
    <Box className={'border-0 h-100'}>
      <HeaderCard text={t(`REPORT.REPORT_NAME.DOCTOR_FEE`)} />
      <Box padding="16px">
        <Grid container spacing="8px">
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => {
                setBranchId(Number(e.target.value));
                setPage(1)
              }}
              options={[
                ...branches.map(b => ({
                  value: b.branchId,
                  name: b.branchName || b.branchNameEn || ''
                }))]}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              label={''}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Box className="position-relative" sx={{ '.MuiOutlinedInput-input': { paddingLeft: '95px !important' } }}>
              <Typography className="position-absolute top-50 translate-middle-y" sx={{ left: '18px', zIndex: 1 }}>
                {t('Code Form')}:
              </Typography>
              <AutocompleteSelect
                labelId="code-form"
                noOptionsText={t('NOT_FOUND')}
                options={operativeCode}
                getOptionLabel={(option: any) => option?.operativeCode === "ALL" ? t('REPORT.REPORT_GROUP.ALL') : (option?.operativeCode || '')}
                renderOption={(props, option: any) => (
                  <Box key={option} component="li" {...props}>
                    {option.operativeCode === "ALL" ? t('REPORT.REPORT_GROUP.ALL') : option.operativeCode}
                  </Box>
                )}
                onchange={(e, value: any) => {
                  setPage(1)
                  setCodeForm(value)
                }}
                value={codeForm}
                height={40}
                disabledBorder
                disableClearable
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Box className="position-relative" sx={{ '.MuiOutlinedInput-input': { paddingLeft: '70px !important' } }}>
              <Typography className="position-absolute top-50 translate-middle-y" sx={{ left: '12px', zIndex: 1 }}>
                {t('Code To')}:
              </Typography>
              <AutocompleteSelect
                labelId="code-to"
                noOptionsText={t('NOT_FOUND')}
                options={operativeCode}
                getOptionLabel={(option: any) => option?.operativeCode === "ALL" ? t('REPORT.REPORT_GROUP.ALL') : (option?.operativeCode || '')}
                renderOption={(props, option: any) => (
                  <Box key={option} component="li" {...props}>
                    {option.operativeCode === "ALL" ? t('REPORT.REPORT_GROUP.ALL') : option.operativeCode}
                  </Box>
                )}
                onchange={(e, value: any) => {
                  setPage(1)
                  setCodeTo(value)
                }}
                value={codeTo}
                height={40}
                disabledBorder
                disableClearable
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={0} lg={4}></Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <SelectCheckBoxInput
              label={t('USER_DOCTOR')}
              options={doctors.map((d: any) => ({
                id: d.userId,
                name: d.fullname
              }))}
              selected={selectDoctors}
              onChange={(ids: any) => {
                setSelectDoctors(ids)
              }}
              fullWidth
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={9} lg={10}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <MainButton
                onClick={() => {
                  setFetch(true)
                  setPage(1)
                  setLoading(true)
                }}
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
              />
              <UseStyled.DropdownDocument>
                <Dropdown.Toggle id="dropdown-basic" className='ml-xl-auto'>
                  {t('PRINT_DOCUMENT')}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleExport}>
                    {t('Excel')}
                  </Dropdown.Item>
                  <FunctionPrint
                    content={() => componentRef.current}
                    docTitle={t(`REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH`)}
                    removeIfram
                    trigger={() => <Dropdown.Item >
                      {t('PDF')}
                    </Dropdown.Item>}
                    style={`@page { size: landscape;  margin: 0.5cm; } @media print { body { -webkit-print-color-adjust: exact; }`}
                  />
                </Dropdown.Menu>
              </UseStyled.DropdownDocument>
            </Box>
          </Grid>
        </Grid>
        <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
          <DoctorFeeList
            page={page}
            pageLimit={pageLimit}
            rowCount={rowCount}
            sortBy={sortBy}
            sortType={sortType}
            data={rows}
            dataExport={rowsExport}
            onRequestSort={onRequestSort}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangePage={handleChangePage}
            setSortType={setSortType}
            setSortBy={setSortBy}
            componentRef={componentRef}
          />
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
    </Box>
  )
}
